import { useState, useRef, useEffect } from "react";
import {
  Users, Clock3, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Code2, Zap, BellRing, HelpCircle,
} from "lucide-react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

const SECTIONS = [
  { id:"overview",   label:"Overview",             icon:"🧵" },
  { id:"problem",    label:"The Problem",           icon:"⚡" },
  { id:"pool",       label:"ThreadPoolExecutor",    icon:"🍦" },
  { id:"futures",    label:"Futures",               icon:"🔔" },
  { id:"mapvsubmit", label:"map() vs submit()",     icon:"⚖️" },
  { id:"quiz",       label:"Quick Quiz",            icon:"❓" },
];

const iceCreamCode = `from concurrent.futures import ThreadPoolExecutor
import time

def serve_customer(customer_name):
    print(f"Scooping ice cream for {customer_name}...")
    time.sleep(1)  # pretend scooping takes 1 second
    return f"{customer_name} got their ice cream!"

customers = [f"Customer-{i}" for i in range(1, 8)]  # 7 customers

# Only 3 scooper stations (3 worker threads)
with ThreadPoolExecutor(max_workers=3) as shop:
    results = shop.map(serve_customer, customers)
    for r in results:
        print(r)`;

const futuresCode = `from concurrent.futures import ThreadPoolExecutor
import time

def cook_order(order_name, burn_it=False):
    time.sleep(1)
    if burn_it:
        raise ValueError(f"{order_name} got burned!")
    return f"{order_name} is ready"

with ThreadPoolExecutor(max_workers=2) as kitchen:
    # submit() returns immediately with a "buzzer" (Future) -- no waiting yet
    buzzer1 = kitchen.submit(cook_order, "Pizza")
    buzzer2 = kitchen.submit(cook_order, "Burger", burn_it=True)

    print("Got my buzzers, I can do other things now...")
    print("Is Pizza done yet?", buzzer1.done())  # probably False

    # .result() blocks -- like standing and waiting for the buzzer
    print(buzzer1.result())

    # If the task raised an exception, .result() re-raises it here
    try:
        print(buzzer2.result())
    except ValueError as e:
        print("Kitchen problem:", e)

    # add_done_callback -- "text me the moment it's ready"
    buzzer3 = kitchen.submit(cook_order, "Fries")
    buzzer3.add_done_callback(lambda f: print("Callback fired:", f.result()))
    time.sleep(1.5)`;

const mapVsSubmitCode = `from concurrent.futures import ThreadPoolExecutor
import time

def task(n):
    time.sleep(0.5)
    return n * 2

with ThreadPoolExecutor(max_workers=3) as ex:

    # --- map() : simple, ordered, no individual control ---
    results = list(ex.map(task, [1, 2, 3, 4, 5]))
    # results are in INPUT order, not completion order
    print("map results:", results)  # [2, 4, 6, 8, 10]

    # --- submit() : flexible, gives you a Future per task ---
    futures = [ex.submit(task, n) for n in [1, 2, 3, 4, 5]]

    # Cancel a future if it hasn't started yet
    futures[-1].cancel()

    for f in futures:
        if not f.cancelled():
            print("submit result:", f.result())`;

const QUIZ = [
  {
    q: "If you call future.result() right after submit(), before the task has even started, does your code crash, hang, or something else — and why?",
    a: "It doesn't crash. It blocks (pauses) your program right there until the task finishes and a result exists — that's the whole point of result(), it waits for the buzzer to go off.",
  },
  {
    q: "Why doesn't map() give you access to individual Futures the way submit() does?",
    a: "map() is a convenience wrapper — it submits everything for you and hands back results directly, in order, once ready. It trades away the individual Future handles for simplicity.",
  },
  {
    q: "If the burger burns and you never call .result() or .exception() on that Future, does your program ever find out something went wrong?",
    a: "No — the exception just sits captured inside the Future silently. It only gets raised (surfaced to you) the moment you call .result() on that specific Future.",
  },
  {
    q: "Was shop.map(serve_customer, customers) internally using Futures behind the scenes, even though you never saw them?",
    a: "Yes. map() calls submit() internally for every item and manages the resulting Futures for you, just without exposing them directly.",
  },
];

function highlight(code) {
  const kws = ["import","from","as","def","class","return","pass","if","else","elif",
    "for","while","in","not","and","or","True","False","None","with","raise","try",
    "except","finally","lambda","yield","super","self","list","print","range","len",
    "sleep","append","format","enumerate","isinstance"];
  const threadKws = ["ThreadPoolExecutor","submit","map","result","done","cancel",
    "add_done_callback","cancelled","exception","Future","max_workers","futures"];
  const tokens_for_line = (line, li) => {
    if (line.trimStart().startsWith("#")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i=line.length; continue;
      }
      if (line[i]==="'" || line[i]==='"') {
        const q=line[i]; let end=i+1;
        while(end<line.length && line[end]!==q) end++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,end+1)}</span>);
        i=end+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.x]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j=i; while(j<line.length && /\w/.test(line[j])) j++;
        const word=line.slice(i,j);
        let color=T.textPri;
        if (kws.includes(word)) color=T.codeKw;
        else if (threadKws.includes(word)) color=T.codeFn;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  };
  return code.split("\n").map(tokens_for_line);
}

function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"5px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"12px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label, color }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
      <Icon style={{width:22,height:22,color: color||T.accent,flexShrink:0}} />
      <h2 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:0}}>{label}</h2>
    </div>
  );
}

function InfoCard({ children, accent }) {
  return (
    <div style={{background:T.card,border:`1px solid ${accent||T.border}`,borderLeft:`3px solid ${accent||T.accent}`,
      borderRadius:10,padding:"14px 18px",marginBottom:12,color:T.textSec,fontSize:14.5,lineHeight:1.7}}>
      {children}
    </div>
  );
}

function BuzzRow({ icon: Icon, iconColor, children }) {
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:12,background:T.card,
      border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
      <Icon style={{width:20,height:20,color:iconColor,flexShrink:0,marginTop:2}} />
      <span style={{fontSize:14,color:T.textSec,lineHeight:1.65}}>{children}</span>
    </div>
  );
}

function Cd({ children }) {
  return (
    <code style={{background:"#1A2235",color:T.codeFn,padding:"1px 6px",borderRadius:4,
      fontFamily:"'JetBrains Mono',monospace",fontSize:"0.88em"}}>{children}</code>
  );
}

function QuizItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden",marginBottom:10}}>
      <button onClick={()=>setOpen(!open)}
        style={{width:"100%",display:"flex",alignItems:"flex-start",justifyContent:"space-between",
          gap:12,textAlign:"left",padding:"14px 16px",background:T.card,border:"none",cursor:"pointer"}}>
        <span style={{display:"flex",gap:12,alignItems:"flex-start"}}>
          <span style={{flexShrink:0,width:24,height:24,borderRadius:"50%",background:"rgba(251,146,60,0.15)",
            color:T.orange,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",marginTop:1}}>
            {index+1}
          </span>
          <span style={{color:T.textPri,fontSize:14.5,fontWeight:500,lineHeight:1.55}}>{item.q}</span>
        </span>
        {open
          ? <ChevronUp style={{width:18,height:18,color:T.textMute,flexShrink:0,marginTop:4}} />
          : <ChevronDown style={{width:18,height:18,color:T.textMute,flexShrink:0,marginTop:4}} />}
      </button>
      {open && (
        <div style={{padding:"0 16px 14px 52px",background:T.surface}}>
          <div style={{background:"rgba(167,139,250,0.08)",border:`1px solid rgba(167,139,250,0.2)`,
            borderRadius:8,padding:"12px 14px",color:T.textSec,fontSize:14,lineHeight:1.65}}>
            {item.a}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PythonThreadPoolFutures() {
  const [active, setActive] = useState("overview");
  const [visited, setVisited] = useState(new Set(["overview"]));
  const mainRef = useRef(null);

  const goTo = (id) => {
    setActive(id);
    setVisited(v => new Set([...v, id]));
    if (mainRef.current) mainRef.current.scrollTop = 0;
  };

  const progress = Math.round((visited.size / SECTIONS.length) * 100);

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,fontFamily:"'Inter',sans-serif"}}>

      {/* ── Sidebar ── */}
      <div style={{width:230,flexShrink:0,background:T.surface,borderRight:`1px solid ${T.border}`,
        overflowY:"auto",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"18px 16px 10px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:T.orange,fontWeight:700,marginBottom:4}}>
            Python Concurrency
          </div>
          <div style={{fontSize:13.5,fontWeight:700,color:T.textPri,lineHeight:1.35}}>
            ThreadPool & Futures
          </div>
        </div>
        <nav style={{padding:"10px 8px",flex:1}}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={()=>goTo(s.id)}
              style={{display:"flex",alignItems:"center",gap:9,width:"100%",textAlign:"left",
                padding:"9px 10px",borderRadius:8,border:"none",cursor:"pointer",marginBottom:3,
                background: active===s.id ? "rgba(56,189,248,0.12)" : "transparent",
                color: active===s.id ? T.accent : visited.has(s.id) ? T.textSec : T.textMute,
                fontSize:13.5,fontWeight: active===s.id ? 600 : 400,
                borderLeft: active===s.id ? `2px solid ${T.accent}` : "2px solid transparent",
                transition:"all 0.15s"}}>
              <span style={{fontSize:15}}>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Main ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Progress bar */}
        <div style={{flexShrink:0,background:T.surface,padding:"8px 36px 10px",
          borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
            <span style={{fontSize:11,color:T.textMute,letterSpacing:.5}}>Progress</span>
            <span style={{fontSize:11,color:T.accent,fontWeight:600}}>{progress}%</span>
          </div>
          <div style={{height:4,background:T.border,borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${progress}%`,background:`linear-gradient(90deg,${T.purple},${T.orange})`,
              borderRadius:2,transition:"width 0.4s"}} />
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>

          {/* ── OVERVIEW ── */}
          {active==="overview" && (
            <div>
              <div style={{marginBottom:28}}>
                <div style={{fontSize:11,letterSpacing:1.2,textTransform:"uppercase",color:T.orange,
                  fontWeight:700,marginBottom:10}}>Python Concurrency · BrainCloud</div>
                <h1 style={{fontSize:30,fontWeight:800,color:T.textPri,margin:"0 0 14px",lineHeight:1.15}}>
                  ThreadPoolExecutor & Futures
                </h1>
                <p style={{color:T.textSec,fontSize:15.5,lineHeight:1.7,maxWidth:"70ch",margin:0}}>
                  No jargon. Just an ice cream shop, a restaurant buzzer, and working Python code
                  you can run yourself right now.
                </p>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:14,marginBottom:28}}>
                {[
                  {icon:Users,color:T.accent,title:"Thread Pool",body:"A fixed team of workers that stay alive and grab the next job — no create/destroy overhead per task."},
                  {icon:Zap,color:T.yellow,title:"The Analogy",body:"An ice cream shop with 3 scooper stations serving 20 customers. The pool is the stations, not the customers."},
                  {icon:BellRing,color:T.purple,title:"Futures",body:"A restaurant buzzer — you get it instantly after ordering and can check it, wait on it, or get a callback."},
                  {icon:Code2,color:T.orange,title:"Two APIs",body:"map() for simple batch jobs. submit() when you need individual control over each task's result."},
                ].map(({ icon: Icon, color, title, body }) => (
                  <div key={title} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:18}}>
                    <Icon style={{width:22,height:22,color,marginBottom:10}} />
                    <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:6}}>{title}</div>
                    <div style={{fontSize:13,color:T.textSec,lineHeight:1.6}}>{body}</div>
                  </div>
                ))}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10}}>
                {SECTIONS.filter(s=>s.id!=="overview").map(s=>(
                  <button key={s.id} onClick={()=>goTo(s.id)}
                    style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
                      padding:"14px 16px",textAlign:"left",cursor:"pointer",color:T.textSec,
                      fontSize:13.5,display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{fontSize:18}}>{s.icon}</span>
                    <span style={{color:T.textPri,fontWeight:500}}>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── THE PROBLEM ── */}
          {active==="problem" && (
            <div>
              <SectionTitle icon={Zap} label="The Problem It Solves" color={T.yellow} />
              <p style={{color:T.textSec,fontSize:15,lineHeight:1.75,maxWidth:"72ch",marginBottom:20}}>
                Imagine you have 100 jobs to do — like "download this file" × 100. You could create
                100 separate threads, but that's like hiring 100 workers for 100 jobs that show up
                over time. It's wasteful: creating a thread takes time and memory.
              </p>
              <p style={{color:T.textSec,fontSize:15,lineHeight:1.75,maxWidth:"72ch",marginBottom:24}}>
                A <strong style={{color:T.accent}}>thread pool</strong> fixes this by keeping a small,
                fixed team of workers who stay around and just keep grabbing the next job.
              </p>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24,maxWidth:"72ch"}}>
                <InfoCard accent={T.red}>
                  <div style={{fontSize:12,letterSpacing:.8,textTransform:"uppercase",color:T.red,marginBottom:8,fontWeight:700}}>
                    Without a pool
                  </div>
                  <ul style={{margin:0,padding:"0 0 0 18px",lineHeight:1.75,fontSize:14}}>
                    <li>Create a new thread per task</li>
                    <li>Thread creation is expensive</li>
                    <li>OS struggles with thousands of threads</li>
                    <li>Memory spikes on high load</li>
                  </ul>
                </InfoCard>
                <InfoCard accent={T.green}>
                  <div style={{fontSize:12,letterSpacing:.8,textTransform:"uppercase",color:T.green,marginBottom:8,fontWeight:700}}>
                    With ThreadPoolExecutor
                  </div>
                  <ul style={{margin:0,padding:"0 0 0 18px",lineHeight:1.75,fontSize:14}}>
                    <li>Fixed workers, reused for each task</li>
                    <li>Built-in task queue</li>
                    <li>Context manager (with statement)</li>
                    <li>Automatic cleanup on exit</li>
                  </ul>
                </InfoCard>
              </div>

              <InfoCard accent={T.purple}>
                <strong style={{color:T.purple}}>Rule of thumb:</strong> for I/O-bound tasks (network,
                disk, APIs), use{" "}
                <Cd>ThreadPoolExecutor</Cd>. For CPU-bound tasks (heavy computation),
                use{" "}
                <Cd>ProcessPoolExecutor</Cd> — threads in Python can't truly run in parallel
                due to the GIL, but they can overlap on I/O waits.
              </InfoCard>
            </div>
          )}

          {/* ── THREADPOOLEXECUTOR ── */}
          {active==="pool" && (
            <div>
              <SectionTitle icon={Users} label="ThreadPoolExecutor — The Ice Cream Shop" color={T.accent} />

              <p style={{color:T.textSec,fontSize:15,lineHeight:1.75,maxWidth:"72ch",marginBottom:20}}>
                Picture an ice cream shop with only <strong style={{color:T.accent}}>3 scooper stations</strong> —
                that's your thread pool with <Cd>max_workers=3</Cd>. Now imagine 7 customers walk in
                — those are your 7 tasks.
              </p>

              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:24,maxWidth:"72ch"}}>
                {[
                  {icon:CheckCircle2,color:T.green,text:"First 3 customers walk straight to the stations and get served immediately."},
                  {icon:Clock3,color:T.yellow,text:"The other 4 join a line (the queue) and wait for a station to free up."},
                  {icon:Zap,color:T.accent,text:"The moment a scooper finishes, they call the next in line — workers get reused, not rehired."},
                ].map(({icon:Icon,color,text})=>(
                  <div key={color} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                    <Icon style={{width:20,height:20,color,marginBottom:10}} />
                    <p style={{fontSize:13,color:T.textSec,margin:0,lineHeight:1.65}}>{text}</p>
                  </div>
                ))}
              </div>

              <InfoCard accent={T.yellow}>
                <strong style={{color:T.yellow}}>Why not hire 7 scoopers for 7 customers?</strong>{" "}
                Creating a thread costs time and memory. For 10,000 tasks, 10,000 threads would crash
                the process. 3 reusable workers handling everyone in turn is far more efficient.
              </InfoCard>

              <div style={{marginTop:24}}>
                <div style={{fontSize:13,color:T.textMute,marginBottom:8,fontWeight:600}}>
                  The ice cream shop in code
                </div>
                <CodeBlock code={iceCreamCode} />
                <InfoCard accent={T.accent}>
                  <strong style={{color:T.accent}}>Run this and watch:</strong> only 3 "Scooping..."
                  lines print before the first "got their ice cream" — proof only 3 customers are ever
                  served at once.
                </InfoCard>
              </div>

              <div style={{marginTop:20}}>
                <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:12}}>
                  What does <Cd>with ThreadPoolExecutor(...) as shop:</Cd> do?
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    ["Creates the pool","Spawns the worker threads when you enter the with block."],
                    ["Runs tasks","Workers pick jobs off the internal queue as they become free."],
                    ["Waits + cleans up","On exit, it blocks until all submitted tasks finish, then shuts down the threads."],
                  ].map(([t,d])=>(
                    <div key={t} style={{display:"flex",gap:14,background:T.card,border:`1px solid ${T.border}`,
                      borderRadius:9,padding:"12px 16px",alignItems:"flex-start"}}>
                      <span style={{fontSize:12,fontWeight:700,color:T.accent,background:"rgba(56,189,248,0.1)",
                        borderRadius:5,padding:"2px 8px",whiteSpace:"nowrap",marginTop:1,fontFamily:"monospace"}}>{t}</span>
                      <span style={{fontSize:14,color:T.textSec,lineHeight:1.6}}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── FUTURES ── */}
          {active==="futures" && (
            <div>
              <SectionTitle icon={BellRing} label="Futures — The Restaurant Buzzer" color={T.purple} />

              <p style={{color:T.textSec,fontSize:15,lineHeight:1.75,maxWidth:"72ch",marginBottom:16}}>
                When you call <Cd>executor.submit(task)</Cd>, Python doesn't wait for the task to finish.
                Instead, it immediately hands you a <strong style={{color:T.purple}}>Future</strong>: an
                object representing "the result that will exist eventually, but might not exist yet."
              </p>
              <p style={{color:T.textSec,fontSize:15,lineHeight:1.75,maxWidth:"72ch",marginBottom:24}}>
                Think of a busy restaurant. When you order, the cashier doesn't make you stand at the
                counter until your food is cooked — they hand you a <strong style={{color:T.yellow}}>buzzer</strong> and
                say "we'll buzz you when it's ready." That buzzer is the Future.
              </p>

              <div style={{marginBottom:20}}>
                <BuzzRow icon={CheckCircle2} iconColor={T.green}>
                  Checking the buzzer without waiting = <Cd>future.done()</Cd>
                </BuzzRow>
                <BuzzRow icon={Clock3} iconColor={T.yellow}>
                  Standing there staring at it until it buzzes = <Cd>future.result()</Cd>
                </BuzzRow>
                <BuzzRow icon={XCircle} iconColor={T.red}>
                  Kitchen burns your order → buzzer tells you when you check = an exception raised by <Cd>.result()</Cd>
                </BuzzRow>
                <BuzzRow icon={BellRing} iconColor={T.purple}>
                  "Text me the moment it's ready" = <Cd>future.add_done_callback(fn)</Cd>
                </BuzzRow>
              </div>

              <InfoCard accent={T.accent}>
                <strong style={{color:T.accent}}>One line to remember:</strong>{" "}
                <Cd>submit()</Cd> gives you a buzzer immediately;{" "}
                <Cd>result()</Cd> is you standing there until it buzzes;{" "}
                <Cd>done()</Cd> is glancing at it without waiting; a callback is "text me instead."
              </InfoCard>

              <div style={{marginTop:24}}>
                <div style={{fontSize:13,color:T.textMute,marginBottom:8,fontWeight:600}}>
                  The restaurant buzzer in code
                </div>
                <CodeBlock code={futuresCode} />
                <InfoCard accent={T.purple}>
                  <strong style={{color:T.purple}}>Run this and watch:</strong> <Cd>done()</Cd> reports <Cd>False</Cd>{" "}
                  right after ordering, and the burned order only raises its error the moment you call <Cd>.result()</Cd> on it.
                </InfoCard>
              </div>

              <div style={{marginTop:24}}>
                <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:12}}>
                  Key Future methods at a glance
                </div>
                <div style={{overflowX:"auto",border:`1px solid ${T.border}`,borderRadius:10}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13.5,minWidth:480}}>
                    <thead>
                      <tr style={{background:T.card}}>
                        {["Method","What it does","Blocks?"].map(h=>(
                          <th key={h} style={{textAlign:"left",padding:"10px 14px",color:T.textMute,
                            fontSize:11,letterSpacing:.8,textTransform:"uppercase",
                            borderBottom:`1px solid ${T.border}`,fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        [".result(timeout=None)","Returns the value, or re-raises the exception","Yes"],
                        [".exception(timeout=None)","Returns the exception, or None if no error","Yes"],
                        [".done()","True if finished (success or error) or cancelled","No"],
                        [".cancel()","Cancels if not yet started","No"],
                        [".cancelled()","True if successfully cancelled","No"],
                        [".add_done_callback(fn)","Calls fn(future) when finished","No"],
                      ].map(([m,d,b])=>(
                        <tr key={m} style={{borderBottom:`1px solid ${T.border}`}}>
                          <td style={{padding:"10px 14px",fontFamily:"'JetBrains Mono',monospace",
                            fontSize:12.5,color:T.codeFn,whiteSpace:"nowrap"}}>{m}</td>
                          <td style={{padding:"10px 14px",color:T.textSec}}>{d}</td>
                          <td style={{padding:"10px 14px",color: b==="Yes" ? T.yellow : T.green,
                            fontFamily:"monospace",fontSize:12}}>{b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── MAP VS SUBMIT ── */}
          {active==="mapvsubmit" && (
            <div>
              <SectionTitle icon={Code2} label="map() vs submit()" color={T.orange} />

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24,maxWidth:"72ch"}}>
                <InfoCard accent={T.accent}>
                  <div style={{fontSize:12,letterSpacing:.8,textTransform:"uppercase",color:T.accent,marginBottom:10,fontWeight:700}}>
                    map()
                  </div>
                  <ul style={{margin:0,padding:"0 0 0 18px",lineHeight:1.8,fontSize:14}}>
                    <li>Like the built-in <Cd>map()</Cd></li>
                    <li>Submits all tasks at once</li>
                    <li>Returns results <strong style={{color:T.textPri}}>in input order</strong></li>
                    <li>No access to individual Futures</li>
                    <li>Simpler — less code</li>
                  </ul>
                </InfoCard>
                <InfoCard accent={T.orange}>
                  <div style={{fontSize:12,letterSpacing:.8,textTransform:"uppercase",color:T.orange,marginBottom:10,fontWeight:700}}>
                    submit()
                  </div>
                  <ul style={{margin:0,padding:"0 0 0 18px",lineHeight:1.8,fontSize:14}}>
                    <li>Returns a <Cd>Future</Cd> per task</li>
                    <li>Can cancel individual tasks</li>
                    <li>Can add callbacks</li>
                    <li>Full control, more code</li>
                    <li>Use with <Cd>as_completed()</Cd> for earliest-first results</li>
                  </ul>
                </InfoCard>
              </div>

              <InfoCard accent={T.yellow}>
                <strong style={{color:T.yellow}}>Quick rule:</strong> use <Cd>map()</Cd> when all tasks take the
                same function and you want results in order. Use <Cd>submit()</Cd> when you need to cancel,
                add callbacks, handle errors per-task, or get results as soon as each finishes.
              </InfoCard>

              <div style={{marginTop:24}}>
                <div style={{fontSize:13,color:T.textMute,marginBottom:8,fontWeight:600}}>
                  Side-by-side comparison
                </div>
                <CodeBlock code={mapVsSubmitCode} />
              </div>

              <div style={{marginTop:20}}>
                <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:12}}>
                  <Cd>as_completed()</Cd> — results as they arrive
                </div>
                <InfoCard accent={T.purple}>
                  <Cd>map()</Cd> waits for everything and returns in order. If you want to process each
                  result the moment it's done (not in input order), use{" "}
                  <Cd>from concurrent.futures import as_completed</Cd>:{" "}
                  <Cd>for future in as_completed(futures): print(future.result())</Cd>
                </InfoCard>
              </div>
            </div>
          )}

          {/* ── QUIZ ── */}
          {active==="quiz" && (
            <div>
              <SectionTitle icon={HelpCircle} label="Quick Quiz" color={T.orange} />
              <p style={{color:T.textSec,fontSize:14,marginBottom:18,lineHeight:1.65}}>
                Try to answer each question before tapping to reveal. Say your reasoning out loud first.
              </p>
              {QUIZ.map((item, i) => <QuizItem key={i} item={item} index={i} />)}

              <div style={{marginTop:28,background:T.card,border:`1px solid ${T.border}`,
                borderLeft:`3px solid ${T.accent}`,borderRadius:10,padding:"18px 20px"}}>
                <div style={{fontSize:12,letterSpacing:.8,textTransform:"uppercase",color:T.accent,
                  fontWeight:700,marginBottom:10}}>Five-line summary</div>
                <ol style={{margin:0,padding:"0 0 0 20px",lineHeight:1.85,color:T.textSec,fontSize:14}}>
                  <li>A thread pool keeps a fixed number of threads alive and reuses them across tasks — no create/destroy overhead.</li>
                  <li><Cd>ThreadPoolExecutor(max_workers=N)</Cd> gives you N workers; extra tasks queue up automatically.</li>
                  <li><Cd>submit(fn, arg)</Cd> returns a <Cd>Future</Cd> immediately — the buzzer you check or wait on later.</li>
                  <li><Cd>future.result()</Cd> blocks until done; <Cd>future.done()</Cd> peeks without blocking.</li>
                  <li><Cd>map()</Cd> is the simple all-at-once shortcut; <Cd>submit()</Cd> gives per-task control via Futures.</li>
                </ol>
              </div>
            </div>
          )}

          {/* Prev / Next */}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:36,paddingTop:24,
            borderTop:`1px solid ${T.border}`}}>
            {(() => {
              const idx = SECTIONS.findIndex(s=>s.id===active);
              const prev = SECTIONS[idx-1];
              const next = SECTIONS[idx+1];
              return (
                <>
                  {prev
                    ? <button onClick={()=>goTo(prev.id)}
                        style={{background:T.card,border:`1px solid ${T.border}`,color:T.textSec,
                          padding:"9px 18px",borderRadius:8,cursor:"pointer",fontSize:14}}>
                        ← {prev.label}
                      </button>
                    : <span/>}
                  {next
                    ? <button onClick={()=>goTo(next.id)}
                        style={{background:T.card,border:`1px solid ${T.accent}`,color:T.accent,
                          padding:"9px 18px",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:600}}>
                        {next.label} →
                      </button>
                    : <span/>}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
